import DOMPurify from "dompurify";

/**
 * Makes chat and AI-generated HTML safe to render inline.
 *
 * Chat bodies come from other people (the Connect BFF stores them exactly as
 * sent) and AI output can carry text injected through an email, so neither may
 * reach `dangerouslySetInnerHTML` raw — a `<img src=x onerror=…>` would run in
 * this session. Email bodies are NOT rendered through here: they go in the
 * sandboxed EmailBodyFrame. Formatting survives; scripts, event handlers,
 * iframes, forms, inline styles and non-http(s)/mailto/relative links do not.
 */
const ALLOWED_TAGS = [
    "a", "b", "strong", "i", "em", "u", "s", "del", "ins", "mark", "small", "sub", "sup",
    "p", "br", "hr", "span", "div", "blockquote", "code", "pre",
    "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
    "table", "thead", "tbody", "tr", "th", "td", "img"
];
const ALLOWED_ATTR = ["href", "title", "alt", "src", "class", "target", "rel", "colspan", "rowspan"];
const ALLOWED_URI = /^(?:https?:|mailto:|data:image\/(?:png|gif|jpe?g|webp);base64,|\/(?!\/)|#)/i;

let hooked = false;

function ensureLinkHook() {
    if (hooked) return;
    hooked = true;
    // Links open in a new tab without giving the target page a handle on ours.
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
}

export function sanitizeHtml(html: string | null | undefined): string {
    if (!html) return "";
    ensureLinkHook();
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOWED_URI_REGEXP: ALLOWED_URI
    });
}
